import React, { useState } from 'react';
import styled from 'styled-components';
import { addPhoto, uploadImage } from '../../services/photoService';
import { PhotoInput } from '../../types/Photo';

const Form = styled.form`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 24px 0;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 150px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
`;

const ImagePreview = styled.div`
  margin-top: 12px;
  max-width: 100%;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const CameraDetailsGroup = styled.div`
  background: #f8f8f8;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 24px;
`;

const CameraDetailsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: #0070f3;
    color: white;

    &:hover:not(:disabled) {
      background: #0051cc;
    }
  `
      : `
    background: #f0f0f0;
    color: #333;

    &:hover:not(:disabled) {
      background: #e0e0e0;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 4px;
  font-size: 14px;

  ${(props) =>
    props.type === 'success'
      ? `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const PhotoUploadForm: React.FC = () => {
  const [formData, setFormData] = useState<Partial<PhotoInput>>({
    title: '',
    description: '',
    location: '',
    date: new Date().toISOString().split('T')[0],
    cameraDetails: {
      make: '',
      model: '',
      lens: '',
      focalLength: '',
      aperture: '',
      shutterSpeed: '',
      iso: '',
    },
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCameraDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      cameraDetails: {
        ...prev.cameraDetails,
        [name]: value,
      },
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!imageFile) {
      setMessage({ type: 'error', text: 'Please select an image to upload' });
      return;
    }

    if (!formData.title || !formData.location) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setUploading(true);

      // Upload image to Firebase Storage
      const imageURL = await uploadImage(imageFile, 'photos');

      // Add photo metadata to Firestore
      const photoData: PhotoInput = {
        imageURL,
        title: formData.title!,
        description: formData.description || '',
        location: formData.location!,
        date: formData.date || new Date().toISOString(),
        cameraDetails: formData.cameraDetails || {},
      };

      await addPhoto(photoData);

      setMessage({ type: 'success', text: 'Photo uploaded successfully!' });

      // Reset form
      setFormData({
        title: '',
        description: '',
        location: '',
        date: new Date().toISOString().split('T')[0],
        cameraDetails: {
          make: '',
          model: '',
          lens: '',
          focalLength: '',
          aperture: '',
          shutterSpeed: '',
          iso: '',
        },
      });
      setImageFile(null);
      setPreviewUrl('');
    } catch (error) {
      console.error('Error uploading photo:', error);
      setMessage({ type: 'error', text: 'Failed to upload photo. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      date: new Date().toISOString().split('T')[0],
      cameraDetails: {
        make: '',
        model: '',
        lens: '',
        focalLength: '',
        aperture: '',
        shutterSpeed: '',
        iso: '',
      },
    });
    setImageFile(null);
    setPreviewUrl('');
    setMessage(null);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Title>Upload New Photo</Title>

      {message && <Message type={message.type}>{message.text}</Message>}

      <FormGroup>
        <Label htmlFor="image">Image *</Label>
        <FileInput
          type="file"
          id="image"
          accept="image/*"
          onChange={handleFileChange}
          required
        />
        {previewUrl && (
          <ImagePreview>
            <img src={previewUrl} alt="Preview" />
          </ImagePreview>
        )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="title">Title *</Label>
        <Input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          placeholder="Enter photo title"
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="location">Location *</Label>
        <Input
          type="text"
          id="location"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          required
          placeholder="e.g., Yosemite National Park, California"
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="date">Date</Label>
        <Input
          type="date"
          id="date"
          name="date"
          value={formData.date as string}
          onChange={handleInputChange}
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="description">Description</Label>
        <TextArea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Tell the story behind this photo..."
        />
      </FormGroup>

      <CameraDetailsGroup>
        <CameraDetailsTitle>Camera Details</CameraDetailsTitle>
        <Row>
          <FormGroup>
            <Label htmlFor="make">Camera Make</Label>
            <Input
              type="text"
              id="make"
              name="make"
              value={formData.cameraDetails?.make}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., Canon"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="model">Camera Model</Label>
            <Input
              type="text"
              id="model"
              name="model"
              value={formData.cameraDetails?.model}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., EOS R5"
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="lens">Lens</Label>
            <Input
              type="text"
              id="lens"
              name="lens"
              value={formData.cameraDetails?.lens}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., RF 24-70mm f/2.8L"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="focalLength">Focal Length</Label>
            <Input
              type="text"
              id="focalLength"
              name="focalLength"
              value={formData.cameraDetails?.focalLength}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., 50mm"
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="aperture">Aperture</Label>
            <Input
              type="text"
              id="aperture"
              name="aperture"
              value={formData.cameraDetails?.aperture}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., f/2.8"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="shutterSpeed">Shutter Speed</Label>
            <Input
              type="text"
              id="shutterSpeed"
              name="shutterSpeed"
              value={formData.cameraDetails?.shutterSpeed}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., 1/250s"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="iso">ISO</Label>
            <Input
              type="text"
              id="iso"
              name="iso"
              value={formData.cameraDetails?.iso}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., ISO 400"
            />
          </FormGroup>
        </Row>
      </CameraDetailsGroup>

      <ButtonGroup>
        <Button type="button" variant="secondary" onClick={handleReset} disabled={uploading}>
          Reset
        </Button>
        <Button type="submit" variant="primary" disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Photo'}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default PhotoUploadForm;
